import React, { useState, useEffect, useCallback, useMemo } from 'react';
import RateShopperModeToggle from './RateShopperModeToggle';
import useIsMobile from '../hooks/useIsMobile';

// Rate Shopper — Competitor Room & Meal-Plan Mapping (Phase 1 / M1).
// Mandatory foundation: map each own (room type + meal plan) to the matching
// competitor (room + meal), per competitor, with priority fallback. No rate
// shopping runs until at least one valid mapping exists.
// Spec: docs/SRS_PRICING_INTELLIGENCE_SUITE.md §10, docs/DEV_RATESHOPPER_PHASE1.md.

const isoToday = (o = 0) => new Date(Date.now() + o * 86400000).toISOString().slice(0, 10);

const CANCELLATION = ['ANY', 'REFUNDABLE', 'NON_REFUNDABLE'];
const TAX_TYPES = ['ANY', 'INCLUSIVE', 'EXCLUSIVE'];
const PAYMENT_TYPES = ['ANY', 'PREPAID', 'PAY_AT_HOTEL'];

const chip = (bg, fg) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11,
  fontWeight: 600, background: bg, color: fg, lineHeight: 1.6, whiteSpace: 'nowrap',
});

function RateShopperMapping({ token, selectedProperty, onSwitchScreen }) {
  const { isMobile } = useIsMobile();
  const grid3Style = isMobile ? { ...grid3, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : grid3;
  const [meal, setMeal] = useState('EP');
  const [mealPlans, setMealPlans] = useState([]);
  const [matrix, setMatrix] = useState({ competitors: [], rows: [] });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Cell drawer state
  const [drawer, setDrawer] = useState(null); // { ownRoomId, ownRoomName, competitor }
  const [drawerMappings, setDrawerMappings] = useState([]);
  const [competitorRooms, setCompetitorRooms] = useState([]);
  const [observedRooms, setObservedRooms] = useState([]); // room_category values seen in the actual rate feed, not yet catalogued
  const [roomPickBusy, setRoomPickBusy] = useState(false); // true while catalouging a feed-observed room the user just picked
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [form, setForm] = useState(null); // edit/create mapping form
  const [newRoomName, setNewRoomName] = useState('');

  // AI suggestion state
  const [suggestFor, setSuggestFor] = useState(null); // competitor object
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);

  // M2 comparison view
  const [view, setView] = useState('mapping');
  const [config, setConfig] = useState({ data_source_provider: 'EASEPMS', own_rate_sync_frequency_hours: 24, has_inventory_rates_url: false });
  const [compareRows, setCompareRows] = useState([]);
  const [compareDays, setCompareDays] = useState(14);
  const [busy, setBusy] = useState('');

  const headers = useCallback((withJson = false) => {
    const h = {
      Authorization: `Bearer ${token}`,
      'x-chain-id': selectedProperty?.chain_id || '',
      'x-property-id': selectedProperty?.id || '',
    };
    if (withJson) h['Content-Type'] = 'application/json';
    return h;
  }, [token, selectedProperty]);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`/api/v1/rate-shopper${path}`, {
      ...opts,
      headers: { ...headers(Boolean(opts.body)), ...(opts.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [headers]);

  const loadAll = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const [mp, mx, st] = await Promise.all([
        api('/meal-plans'),
        api(`/mappings/matrix?meal=${encodeURIComponent(meal)}`),
        api('/mappings/status'),
      ]);
      setMealPlans(mp.meal_plans || []);
      setMatrix({ competitors: mx.competitors || [], rows: mx.rows || [] });
      setStatus(st);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }, [selectedProperty, api, meal]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3500); };

  /* ─── M2: config + comparison ─────────────────────────── */
  const loadConfig = useCallback(async () => {
    try { setConfig(await api('/config')); } catch { /* non-fatal */ }
  }, [api]);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const loadCompare = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setBusy('compare');
    try {
      const r = await api(`/rates/compare?meal=${encodeURIComponent(meal)}&from=${isoToday(0)}&to=${isoToday(compareDays - 1)}`);
      setCompareRows(r.rows || []);
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }, [api, meal, compareDays, selectedProperty]);
  useEffect(() => { if (view === 'comparison') loadCompare(); }, [view, loadCompare]);

  const saveConfig = async (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    try { await api('/config', { method: 'POST', body: JSON.stringify(next) }); flash('Data source saved'); }
    catch (err) { setError(err.message); }
  };
  const rebuildSnapshots = async () => {
    setBusy('rebuild');
    try {
      const r = await api('/snapshots/rebuild', { method: 'POST', body: JSON.stringify({ days: compareDays }) });
      flash(`Rebuilt ${r.written} snapshots · ${r.comparable} comparable · ${r.nc} N/C`);
      await loadCompare();
    } catch (err) { setError(err.message); } finally { setBusy(''); }
  };
  const syncOwnRates = async () => {
    setBusy('sync');
    try {
      const r = await api('/own-rates/sync', { method: 'POST', body: JSON.stringify({ days: compareDays }) });
      flash(`Own rates synced: ${r.own_rate_pull?.cached || 0} cached · ${r.snapshots?.written || 0} snapshots`);
      await loadCompare();
    } catch (err) { setError(err.message); } finally { setBusy(''); }
  };

  /* ─── cell drawer ─────────────────────────────────────── */
  const openCell = async (row, competitor) => {
    setDrawer({ ownRoomId: row.own_room_id, ownRoomName: row.own_room_name, competitor });
    setForm(null); setNewRoomName('');
    setDrawerLoading(true);
    try {
      const [maps, rooms, observed] = await Promise.all([
        api(`/mappings?own_room_id=${row.own_room_id}&competitor_id=${competitor.competitor_id}`),
        api(`/competitors/${competitor.competitor_id}/rooms`),
        api(`/competitors/${competitor.competitor_id}/observed-rooms`).catch(() => ({ observed_rooms: [] })),
      ]);
      setDrawerMappings((maps.mappings || []).filter((m) => m.own_meal_plan_code === meal));
      setCompetitorRooms(rooms.rooms || []);
      setObservedRooms(observed.observed_rooms || []);
    } catch (err) {
      setError(err.message);
    } finally { setDrawerLoading(false); }
  };

  const closeDrawer = () => { setDrawer(null); setForm(null); setDrawerMappings([]); setCompetitorRooms([]); setObservedRooms([]); setRoomPickBusy(false); };

  const startNewMapping = (priority) => {
    setForm({
      id: null, own_meal_plan_code: meal, competitor_meal_code: meal,
      competitor_room_id: competitorRooms[0]?.id || '', priority,
      occupancy: '', adults: '', children: '',
      cancellation_policy: 'ANY', tax_type: 'ANY', payment_type: 'ANY',
      effective_from: '', effective_to: '', confidence_score: '',
    });
  };

  const editMapping = (m) => setForm({
    id: m.id, own_meal_plan_code: m.own_meal_plan_code, competitor_meal_code: m.competitor_meal_code,
    competitor_room_id: m.competitor_room_id || '', priority: m.priority,
    occupancy: m.occupancy ?? '', adults: m.adults ?? '', children: m.children ?? '',
    cancellation_policy: m.cancellation_policy || 'ANY', tax_type: m.tax_type || 'ANY',
    payment_type: m.payment_type || 'ANY', effective_from: m.effective_from || '',
    effective_to: m.effective_to || '', confidence_score: m.confidence_score ?? '',
  });

  const addCompetitorRoomNamed = async (roomName, source) => {
    const r = await api(`/competitors/${drawer.competitor.competitor_id}/rooms`, {
      method: 'POST', body: JSON.stringify({ room_name: roomName, source }),
    });
    const [rooms, observed] = await Promise.all([
      api(`/competitors/${drawer.competitor.competitor_id}/rooms`),
      api(`/competitors/${drawer.competitor.competitor_id}/observed-rooms`).catch(() => ({ observed_rooms: [] })),
    ]);
    setCompetitorRooms(rooms.rooms || []);
    setObservedRooms(observed.observed_rooms || []);
    if (form) setForm({ ...form, competitor_room_id: r.id });
    return r;
  };

  const addCompetitorRoom = async () => {
    if (!newRoomName.trim()) return;
    try {
      await addCompetitorRoomNamed(newRoomName.trim(), 'MANUAL');
      setNewRoomName('');
      flash('Competitor room added');
    } catch (err) { setError(err.message); }
  };

  // Selecting from the Competitor room dropdown. A plain value is an existing
  // rs_competitor_room id; an "obs:<name>" value is a room seen in the partner
  // rate feed but not catalogued yet -- catalogue it now (source=SHOP) so it
  // resolves to a real id, guaranteeing it matches what the feed sends.
  const pickCompetitorRoom = async (value) => {
    if (!value) { setForm({ ...form, competitor_room_id: '' }); return; }
    if (value.startsWith('obs:')) {
      setRoomPickBusy(true);
      try {
        const r = await addCompetitorRoomNamed(value.slice(4), 'SHOP');
        setForm((f) => ({ ...f, competitor_room_id: r.id }));
      } catch (err) { setError(err.message); }
      finally { setRoomPickBusy(false); }
      return;
    }
    setForm({ ...form, competitor_room_id: value });
  };

  const saveMapping = async () => {
    let competitorRoomId = form.competitor_room_id;
    // If the hotelier typed a room name but never clicked the separate "Add"
    // button, create it now instead of rejecting the save — typing a name and
    // hitting Save is the flow people actually expect.
    if (!competitorRoomId && newRoomName.trim()) {
      try {
        const r = await addCompetitorRoomNamed(newRoomName.trim(), 'MANUAL');
        competitorRoomId = r.id;
        setNewRoomName('');
      } catch (err) { setError(err.message); return; }
    }
    if (!competitorRoomId) { setError('Pick a competitor room'); return; }
    try {
      const body = {
        own_room_id: drawer.ownRoomId, competitor_id: drawer.competitor.competitor_id,
        competitor_room_id: Number(competitorRoomId),
        own_meal_plan_code: form.own_meal_plan_code, competitor_meal_code: form.competitor_meal_code,
        occupancy: form.occupancy, adults: form.adults, children: form.children,
        cancellation_policy: form.cancellation_policy, tax_type: form.tax_type, payment_type: form.payment_type,
        priority: form.priority, effective_from: form.effective_from || null,
        effective_to: form.effective_to || null, confidence_score: form.confidence_score,
      };
      if (form.id) await api(`/mappings/${form.id}`, { method: 'PUT', body: JSON.stringify(body) });
      else await api('/mappings', { method: 'POST', body: JSON.stringify(body) });
      setForm(null);
      await openCell({ own_room_id: drawer.ownRoomId, own_room_name: drawer.ownRoomName }, drawer.competitor);
      await loadAll();
      flash('Mapping saved');
    } catch (err) { setError(err.message); }
  };

  const deleteMapping = async (id) => {
    try {
      await api(`/mappings/${id}`, { method: 'DELETE' });
      await openCell({ own_room_id: drawer.ownRoomId, own_room_name: drawer.ownRoomName }, drawer.competitor);
      await loadAll();
      flash('Mapping removed');
    } catch (err) { setError(err.message); }
  };

  /* ─── AI suggest ──────────────────────────────────────── */
  const runSuggest = async (competitor) => {
    setSuggestFor(competitor); setSuggestions([]); setAiUsed(false); setSuggestLoading(true);
    try {
      const r = await api('/mappings/suggest', {
        method: 'POST',
        body: JSON.stringify({ competitor_id: competitor.competitor_id, own_meal_plan_code: meal }),
      });
      setSuggestions((r.suggestions || []).map((s) => ({ ...s, _accept: Boolean(s.competitor_room_id) })));
      setAiUsed(Boolean(r.ai_used));
    } catch (err) { setError(err.message); }
    finally { setSuggestLoading(false); }
  };

  const applySuggestions = async () => {
    const chosen = suggestions.filter((s) => s._accept && s.competitor_room_id);
    if (!chosen.length) { setSuggestFor(null); return; }
    try {
      for (const s of chosen) {
        await api('/mappings', {
          method: 'POST',
          body: JSON.stringify({
            own_room_id: s.own_room_id, competitor_id: suggestFor.competitor_id,
            competitor_room_id: s.competitor_room_id, own_meal_plan_code: meal,
            competitor_meal_code: meal, priority: 1, confidence_score: s.confidence_score,
            mapping_source: 'AI_SUGGESTED',
          }),
        });
      }
      setSuggestFor(null);
      await loadAll();
      flash(`Applied ${chosen.length} mapping${chosen.length > 1 ? 's' : ''}`);
    } catch (err) { setError(err.message); }
  };

  /* ─── derived ─────────────────────────────────────────── */
  const kpis = useMemo(() => {
    if (!status) return null;
    return [
      { label: 'Own room types', value: status.own_rooms },
      { label: 'Competitors', value: status.competitors },
      { label: 'Meal plans', value: status.meal_plans },
      { label: 'Mapped pairs', value: status.mapped_pairs },
      { label: 'Coverage', value: `${status.coverage_pct}%` },
    ];
  }, [status]);

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Select a property to configure competitor mappings.</div>;
  }

  const gateOk = status?.shopping_enabled;
  const compCount = matrix.competitors.length;

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: '100%' }}>
      <RateShopperModeToggle mode="mapping" onSwitch={onSwitchScreen} />
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Competitor Room &amp; Meal-Plan Mapping</h2>
          <div style={{ color: 'var(--theme-text-muted)', fontSize: 13, marginTop: 2 }}>
            Map each own room + meal plan to the matching competitor product for apple-to-apple rate comparison.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>Meal plan</label>
          <select value={meal} onChange={(e) => setMeal(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)' }}>
            {(mealPlans.length ? mealPlans : [{ code: 'EP', label: 'European Plan' }]).map((mp) => (
              <option key={mp.code} value={mp.code}>{mp.code} — {mp.label}</option>
            ))}
          </select>
          <button onClick={loadAll} style={btn('ghost')}>↻ Refresh</button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 6, margin: '10px 0 4px', borderBottom: '1px solid var(--theme-border)', paddingBottom: 8 }}>
        {[['mapping', 'Mapping'], ['comparison', 'Comparison']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} style={btn(view === k ? 'primary' : 'ghost')}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {message && <div style={banner('success')}>{message}</div>}

      {view === 'mapping' && (<>
      {/* Gate banner */}
      <div style={{
        margin: '12px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13,
        border: `1px solid ${gateOk ? 'var(--theme-success)' : '#ca8a04'}`,
        background: gateOk ? 'rgba(22,163,74,0.08)' : 'rgba(202,138,4,0.08)',
        color: 'var(--theme-text)', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>{gateOk ? '✅' : '⚠️'}</span>
        {gateOk
          ? <span><b>Rate shopping enabled.</b> At least one active mapping exists. Competitor rates will compare on the mapped products.</span>
          : <span><b>Rate shopping is blocked.</b> Create at least one competitor mapping below before rates can be compared apple-to-apple.</span>}
      </div>

      {/* KPI strip */}
      {kpis && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {kpis.map((k) => (
            <div key={k.label} style={{
              flex: '1 1 120px', minWidth: 120, padding: '10px 14px', borderRadius: 8,
              border: '1px solid var(--theme-border)', background: 'var(--theme-surface)',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Matrix */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-text-muted)' }}>Loading mapping matrix…</div>
      ) : compCount === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-muted)', border: '1px dashed var(--theme-border)', borderRadius: 8 }}>
          No competitors configured yet. Add competitors in <b>Settings → Competitor Rate API</b> (or the Revenue Manager comp set) first, then map their rooms here.
        </div>
      ) : matrix.rows.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-muted)', border: '1px dashed var(--theme-border)', borderRadius: 8 }}>
          No own room types found for this property. Configure room types in PMS Masters first.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, minWidth: 180, background: 'var(--theme-surface-2, #f1f5f9)' }}>Own room ▸ Competitor</th>
                {matrix.competitors.map((c) => (
                  <th key={c.id} style={{ ...thStyle, minWidth: 170 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontWeight: 700 }}>{c.hotel_name}</span>
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--theme-text-muted)' }}>{c.city}</span>
                      <button onClick={() => runSuggest({ competitor_id: c.id, hotel_name: c.hotel_name })}
                        style={{ ...btn('ai'), fontSize: 11, padding: '3px 8px', alignSelf: 'flex-start' }}>✨ AI suggest</button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row) => (
                <tr key={row.own_room_id}>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, zIndex: 2, fontWeight: 600, background: 'var(--theme-surface)' }}>
                    {row.own_room_name}
                  </td>
                  {row.cells.map((cell) => {
                    const mapped = cell.status === 'MAPPED';
                    return (
                      <td key={cell.competitor_id} style={{ ...tdStyle, cursor: 'pointer' }}
                        onClick={() => openCell(row, { ...cell, hotel_name: matrix.competitors.find((c) => c.id === cell.competitor_id)?.hotel_name || 'Competitor' })}
                        title="Click to edit mapping">
                        {mapped ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={chip('rgba(22,163,74,0.15)', 'var(--theme-success)')}>
                              ✓ {cell.primary?.competitor_room_name || 'Mapped'}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>
                              {cell.primary?.competitor_meal_code}
                              {cell.priorities > 1 ? ` · +${cell.priorities - 1} fallback` : ''}
                              {cell.primary?.confidence_score != null ? ` · ${Math.round(cell.primary.confidence_score)}%` : ''}
                              {cell.primary?.ai ? ' · AI' : ''}
                            </span>
                          </div>
                        ) : (
                          <span style={chip('rgba(148,163,184,0.18)', 'var(--theme-text-muted)')}>+ Map</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {/* Comparison view (M2) */}
      {view === 'comparison' && (
        <div style={{ marginTop: 12 }}>
          {/* Data-source + actions bar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', padding: 12, border: '1px solid var(--theme-border)', borderRadius: 8, marginBottom: 12 }}>
            <div>
              <label style={lbl}>Own-rate data source</label>
              <select value={config.data_source_provider} onChange={(e) => saveConfig({ data_source_provider: e.target.value })}
                style={{ ...inp, width: 200 }}>
                <option value="EASEPMS">EASEPMS (PMS rate plans)</option>
                <option value="EASEROOM_CM">EASEROOM Channel Manager</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Horizon (days)</label>
              <select value={compareDays} onChange={(e) => setCompareDays(Number(e.target.value))} style={{ ...inp, width: 120 }}>
                {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
              </select>
            </div>
            <button onClick={rebuildSnapshots} disabled={busy === 'rebuild'} style={btn('primary')}>
              {busy === 'rebuild' ? 'Rebuilding…' : '↻ Rebuild snapshots'}
            </button>
            {config.data_source_provider === 'EASEROOM_CM' && (
              <button onClick={syncOwnRates} disabled={busy === 'sync'} style={btn('ghost')}
                title={config.has_inventory_rates_url ? '' : 'Set the Get Inventory & Rates URL in Channel Manager Settings first'}>
                {busy === 'sync' ? 'Syncing…' : '⭳ Sync own rates (CM)'}
              </button>
            )}
            <button onClick={loadCompare} disabled={busy === 'compare'} style={btn('ghost')}>↻ Refresh</button>
          </div>

          {config.data_source_provider === 'EASEROOM_CM' && !config.has_inventory_rates_url && (
            <div style={banner('danger')}>
              EASEROOM CM selected but the <b>Get Inventory &amp; Rates URL</b> isn’t configured. Add it in <b>Settings → Channel Manager</b> before syncing own rates.
            </div>
          )}

          {/* Comparison grid */}
          {busy === 'compare' ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-muted)' }}>Loading comparison…</div>
          ) : compareRows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-muted)', border: '1px dashed var(--theme-border)', borderRadius: 8 }}>
              No comparison data yet. Ensure mappings exist, competitor rates have arrived, then <b>Rebuild snapshots</b>.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2, minWidth: 150 }}>Room</th>
                    <th style={thStyle}>Date</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Own rate</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Market median</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Δ vs median</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Rank</th>
                    <th style={{ ...thStyle, minWidth: 240 }}>Competitors (mapped)</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((r, i) => {
                    const own = r.own_rate;
                    const med = r.market_median;
                    const delta = (own != null && med != null) ? own - med : null;
                    return (
                      <tr key={`${r.own_room_id}|${r.stay_date}|${i}`}>
                        <td style={{ ...tdStyle, position: 'sticky', left: 0, background: 'var(--theme-surface)', fontWeight: 600 }}>{r.own_room_name}</td>
                        <td style={tdStyle}>{r.stay_date}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{own != null ? `₹${Number(own).toLocaleString('en-IN')}` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{med != null ? `₹${Number(med).toLocaleString('en-IN')}` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: delta == null ? 'var(--theme-text-muted)' : delta > 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                          {delta == null ? '—' : `${delta > 0 ? '+' : ''}₹${Number(delta).toLocaleString('en-IN')}`}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {r.own_rank != null ? <span style={chip('rgba(37,99,235,0.15)', '#2563eb')}>#{r.own_rank}/{r.comparable_count + 1}</span> : '—'}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {r.competitors.map((c) => c.is_comparable && c.rate != null ? (
                              <span key={c.competitor_id} style={chip('rgba(22,163,74,0.12)', 'var(--theme-success)')} title={`${c.hotel_name} · ${c.competitor_room_name || ''} · ${c.channel || ''} · P${c.priority_used}`}>
                                {c.hotel_name.split(' ')[0]}: ₹{Number(c.rate).toLocaleString('en-IN')}
                              </span>
                            ) : (
                              <span key={c.competitor_id} style={chip('rgba(148,163,184,0.18)', 'var(--theme-text-muted)')} title={`${c.hotel_name} · not comparable`}>
                                {c.hotel_name.split(' ')[0]}: {c.nc_reason || 'N/C'}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cell drawer */}
      {drawer && (
        <div style={overlay} onClick={closeDrawer}>
          <div style={drawerPanel} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>{drawer.ownRoomName} <span style={{ color: 'var(--theme-text-muted)', fontWeight: 400 }}>({meal})</span></h3>
                <div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>↔ {drawer.competitor.hotel_name}</div>
              </div>
              <button onClick={closeDrawer} style={btn('ghost')}>✕</button>
            </div>

            {drawerLoading ? (
              <div style={{ padding: 20, color: 'var(--theme-text-muted)' }}>Loading…</div>
            ) : (
              <>
                {/* Existing priorities */}
                <div style={{ margin: '10px 0' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Mappings (priority order)</div>
                  {drawerMappings.length === 0 && !form && (
                    <div style={{ fontSize: 13, color: 'var(--theme-text-muted)', padding: '8px 0' }}>No mapping yet for this pair.</div>
                  )}
                  {drawerMappings.sort((a, b) => a.priority - b.priority).map((m) => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--theme-border)', borderRadius: 6, marginTop: 6 }}>
                      <span style={chip('rgba(37,99,235,0.15)', '#2563eb')}>P{m.priority}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{m.competitor_room_name || '—'} · {m.competitor_meal_code}</div>
                        <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>
                          {[m.cancellation_policy, m.tax_type, m.payment_type].filter((x) => x && x !== 'ANY').join(' · ') || 'any conditions'}
                          {m.confidence_score != null ? ` · ${Math.round(m.confidence_score)}%` : ''}
                          {m.mapping_source === 'AI_SUGGESTED' ? ' · AI' : ''}
                        </div>
                      </div>
                      <button onClick={() => editMapping(m)} style={btn('ghost')}>Edit</button>
                      <button onClick={() => deleteMapping(m.id)} style={btn('dangerGhost')}>Remove</button>
                    </div>
                  ))}
                  {!form && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => startNewMapping(1)} style={btn('primary')}>+ Primary (P1)</button>
                      <button onClick={() => startNewMapping((drawerMappings.reduce((mx, m) => Math.max(mx, m.priority), 0) || 0) + 1)}
                        style={btn('ghost')} disabled={!drawerMappings.length}>+ Fallback</button>
                    </div>
                  )}
                </div>

                {/* Editor form */}
                {form && (
                  <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--theme-border)', borderRadius: 8, background: 'var(--theme-surface-2, #f8fafc)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{form.id ? 'Edit mapping' : 'New mapping'} · Priority {form.priority}</div>

                    {/* Competitor room picker — catalogued rooms + rooms already seen in
                        the partner rate feed (auto-catalogued the moment you pick one) */}
                    <label style={lbl}>Competitor room</label>
                    <select value={form.competitor_room_id} onChange={(e) => pickCompetitorRoom(e.target.value)}
                      disabled={roomPickBusy} style={inp}>
                      <option value="">{roomPickBusy ? 'Adding…' : '— select —'}</option>
                      {competitorRooms.length > 0 && (
                        <optgroup label="Catalogued">
                          {competitorRooms.map((cr) => <option key={cr.id} value={cr.id}>{cr.room_name}</option>)}
                        </optgroup>
                      )}
                      {observedRooms.length > 0 && (
                        <optgroup label="Seen in rate feed">
                          {observedRooms.map((o) => (
                            <option key={o.room_name} value={`obs:${o.room_name}`}>
                              {o.room_name} ({o.rate_rows} rate row{o.rate_rows === 1 ? '' : 's'})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <input value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)}
                        placeholder="Not in the list? Type a new competitor room…" style={{ ...inp, marginTop: 0 }} />
                      <button onClick={addCompetitorRoom} style={btn('ghost')}>Add</button>
                    </div>

                    <div style={grid2}>
                      <div>
                        <label style={lbl}>Competitor meal</label>
                        <select value={form.competitor_meal_code} onChange={(e) => setForm({ ...form, competitor_meal_code: e.target.value })} style={inp}>
                          {(mealPlans.length ? mealPlans : [{ code: 'EP' }]).map((mp) => <option key={mp.code} value={mp.code}>{mp.code}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Priority</label>
                        <input type="number" min={1} max={9} value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} style={inp} />
                      </div>
                    </div>

                    <div style={grid3Style}>
                      <div><label style={lbl}>Occupancy</label><input type="number" value={form.occupancy} onChange={(e) => setForm({ ...form, occupancy: e.target.value })} style={inp} /></div>
                      <div><label style={lbl}>Adults</label><input type="number" value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} style={inp} /></div>
                      <div><label style={lbl}>Children</label><input type="number" value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} style={inp} /></div>
                    </div>

                    <div style={grid3Style}>
                      <div>
                        <label style={lbl}>Cancellation</label>
                        <select value={form.cancellation_policy} onChange={(e) => setForm({ ...form, cancellation_policy: e.target.value })} style={inp}>
                          {CANCELLATION.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Tax</label>
                        <select value={form.tax_type} onChange={(e) => setForm({ ...form, tax_type: e.target.value })} style={inp}>
                          {TAX_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Payment</label>
                        <select value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} style={inp}>
                          {PAYMENT_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={grid2}>
                      <div><label style={lbl}>Effective from</label><input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} style={inp} /></div>
                      <div><label style={lbl}>Effective to</label><input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} style={inp} /></div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={saveMapping} style={btn('primary')}>Save mapping</button>
                      <button onClick={() => setForm(null)} style={btn('ghost')}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* AI suggestions modal */}
      {suggestFor && (
        <div style={overlay} onClick={() => setSuggestFor(null)}>
          <div style={{ ...drawerPanel, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>✨ Suggested mappings · {suggestFor.hotel_name}</h3>
              <button onClick={() => setSuggestFor(null)} style={btn('ghost')}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 8 }}>
              {aiUsed ? 'AI-assisted matches' : 'Heuristic matches'} for meal plan <b>{meal}</b>. Review, uncheck any you disagree with, then apply.
            </div>
            {suggestLoading ? (
              <div style={{ padding: 20, color: 'var(--theme-text-muted)' }}>Analysing rooms…</div>
            ) : suggestions.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--theme-text-muted)' }}>No competitor rooms catalogued yet — add rooms in a cell first, or run a rate shop.</div>
            ) : (
              <>
                <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {suggestions.map((s) => (
                    <div key={s.own_room_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderBottom: '1px solid var(--theme-border)' }}>
                      <input type="checkbox" checked={s._accept} disabled={!s.competitor_room_id}
                        onChange={(e) => setSuggestions((prev) => prev.map((x) => x.own_room_id === s.own_room_id ? { ...x, _accept: e.target.checked } : x))} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{s.own_room_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
                          → {s.competitor_room_name || <i>no comparable room</i>}
                        </div>
                      </div>
                      {s.competitor_room_id && (
                        <span style={chip(
                          s.confidence_score >= 70 ? 'rgba(22,163,74,0.15)' : s.confidence_score >= 40 ? 'rgba(202,138,4,0.15)' : 'rgba(239,68,68,0.12)',
                          s.confidence_score >= 70 ? 'var(--theme-success)' : s.confidence_score >= 40 ? '#ca8a04' : 'var(--theme-danger)'
                        )}>{Math.round(s.confidence_score)}%</span>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={applySuggestions} style={btn('primary')}>Apply selected as Priority 1</button>
                  <button onClick={() => setSuggestFor(null)} style={btn('ghost')}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── styles ──────────────────────────────────────────────── */
const thStyle = { textAlign: 'left', padding: '10px 12px', fontSize: 12, borderBottom: '1px solid var(--theme-border)', background: 'var(--theme-surface-2, #f1f5f9)', color: 'var(--theme-text)' };
const tdStyle = { padding: '8px 12px', fontSize: 13, borderBottom: '1px solid var(--theme-border)', borderRight: '1px solid var(--theme-border)', verticalAlign: 'top' };
const lbl = { display: 'block', fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 8, marginBottom: 2, fontWeight: 600 };
const inp = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)', fontSize: 13, boxSizing: 'border-box', marginTop: 2 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const drawerPanel = { width: 'min(480px, 100%)', maxWidth: 480, height: '100%', overflowY: 'auto', background: 'var(--theme-surface, #ffffff)', color: 'var(--theme-text, #374151)', padding: 18, boxShadow: '-4px 0 24px rgba(0,0,0,0.2)' };

function banner(kind) {
  const map = {
    danger: { bg: 'rgba(239,68,68,0.1)', bd: 'var(--theme-danger)', fg: 'var(--theme-danger)' },
    success: { bg: 'rgba(22,163,74,0.1)', bd: 'var(--theme-success)', fg: 'var(--theme-success)' },
  }[kind];
  return { margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: 13, background: map.bg, border: `1px solid ${map.bd}`, color: map.fg };
}
function btn(kind) {
  const base = { padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid transparent' };
  const map = {
    primary: { background: 'var(--theme-primary)', color: '#fff', borderColor: 'var(--theme-primary)' },
    ghost: { background: 'transparent', color: 'var(--theme-text)', borderColor: 'var(--theme-border)' },
    dangerGhost: { background: 'transparent', color: 'var(--theme-danger)', borderColor: 'var(--theme-border)' },
    ai: { background: 'rgba(124,58,237,0.12)', color: '#7c3aed', borderColor: 'rgba(124,58,237,0.3)' },
  };
  return { ...base, ...map[kind] };
}

export default RateShopperMapping;
